/**
 * Refetches what the viewer's RLS resolved (ADR 0091) when the viewer's OWN
 * membership changes (office, role or status) in a membership read: cited
 * evidence, knowledge-bucket signed URLs and downloads, knowledge lists, and a
 * document's sources and origin.
 * On-screen entries are reset and refetched, so they show as pending until the
 * refetch answers rather than keep what the previous access resolved; entries
 * off screen are dropped, so their next mount reads afresh (the app does not
 * refetch on mount by default). A read already in flight is cancelled first,
 * so a response authorised before the change never lands after it. A membership refresh that changes nothing, or changes only
 * colleagues' rows or profile fields, does nothing, and changes that arrive
 * together are applied once. The membership watcher does not react to
 * knowledge mutations: they are the viewer's own, and the evidence reader
 * rechecks on mount, focus and every minute.
 *
 * Knowledge Spaces (ADR 0091) adds one two-phase notifier for access changes,
 * matched by office and actor: space and document access mutations call it,
 * and under the confirmed gate so does a change to the viewer's own
 * membership. With the gate off, unknown or unconfirmed the membership path
 * is unchanged. See
 * docs/plans/2026-10-20-feat-knowledge-spaces-library-and-access-admin-plan.md
 * (U2 and section 8).
 */
import {
  DRAFT_SOURCE_EVIDENCE_QUERY_KEY,
  KNOWLEDGE_QUERY_KEY,
  SENT_DRAFT_CITATIONS_QUERY_KEY,
} from '@/hooks/pii-query-key-roots';
import { isDocumentProvenanceKey } from '@/hooks/use-document-sources';
import {
  matchesKnowledgeFamilies,
  type KnowledgeFamily,
  type KnowledgeFamilyScope,
} from '@/hooks/use-knowledge-keys';
import { isKnowledgeStorageKey } from '@/hooks/use-storage-file';

import type { Query, QueryClient } from '@tanstack/react-query';

/** Every query root that reads membership rows (user_id, role, status). */
const MEMBERSHIP_ROOTS = new Set([
  'organization-members-cache',
  'organization-memberships',
  'organization-memberships-by-id',
  'organization-memberships-by-slug',
  'user-organizations',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * What decides this viewer's access in a membership payload: the offices they
 * belong to, with role and status. Null when the payload is not a row list.
 */
function ownMembershipProjection(data: unknown, actorId: string): string | null {
  if (!Array.isArray(data)) {
    return null;
  }
  return data
    .filter((row): row is Record<string, unknown> => isRecord(row) && row.user_id === actorId)
    .map((row) => `${text(row.organization_id)}|${text(row.role)}|${text(row.status)}`)
    .sort((a, b) => a.localeCompare(b))
    .join('\n');
}

/** Entries whose content follows the viewer's access. */
function isAccessScopedEntry(query: Query): boolean {
  const key = query.queryKey;
  return (
    key[0] === DRAFT_SOURCE_EVIDENCE_QUERY_KEY ||
    key[0] === SENT_DRAFT_CITATIONS_QUERY_KEY ||
    key[0] === KNOWLEDGE_QUERY_KEY ||
    isKnowledgeStorageKey(key) ||
    isDocumentProvenanceKey(key)
  );
}

async function refetchAccessScopedEntries(
  client: QueryClient,
  actorId: string,
  isActive: () => boolean,
): Promise<void> {
  const predicate = (query: Query): boolean =>
    isActive() && isAccessScopedEntry(query) && query.queryKey.includes(actorId);
  // Cancelled first: a read in flight may predate the change.
  await client.cancelQueries({ predicate });
  if (!isActive()) {
    return;
  }
  client.removeQueries({
    predicate: (query) => predicate(query) && query.getObserversCount() === 0,
  });
  if (!isActive()) {
    return;
  }
  await client.resetQueries({ predicate });
}

export function subscribeToEvidenceInvalidation(
  client: QueryClient,
  actorId: string | null,
  onAccessChange?: () => void,
): () => void {
  if (actorId === null) {
    return () => {
      /* nothing was subscribed */
    };
  }
  // Kept after a membership query is evicted, so the read that repopulates it
  // is still compared with what this viewer's access was before.
  const seen = new Map<string, string | null>();
  let scheduled = false;
  let active = true;

  // Reads cached before this subscription are the baseline a later change is
  // measured against.
  for (const root of MEMBERSHIP_ROOTS) {
    for (const query of client.getQueryCache().findAll({ queryKey: [root] })) {
      if (query.state.data !== undefined) {
        seen.set(query.queryHash, ownMembershipProjection(query.state.data, actorId));
      }
    }
  }

  const schedule = (): void => {
    if (scheduled) {
      return;
    }
    scheduled = true;
    onAccessChange?.();
    queueMicrotask(() => {
      scheduled = false;
      if (active && notifyGatedMembershipChange(client, actorId)) {
        return;
      }
      if (active) {
        refetchAccessScopedEntries(client, actorId, () => active).catch(() => {
          /* A failed refetch surfaces through each query's own error state. */
        });
      }
    });
  };

  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') {
      return;
    }
    const root = (event.query.queryKey as readonly unknown[])[0];
    if (typeof root !== 'string' || !MEMBERSHIP_ROOTS.has(root)) {
      return;
    }
    const projection = ownMembershipProjection(event.query.state.data as unknown, actorId);
    const known = seen.has(event.query.queryHash);
    const previous = seen.get(event.query.queryHash);
    seen.set(event.query.queryHash, projection);
    if (known && previous !== projection) {
      schedule();
    }
  });

  return () => {
    active = false;
    unsubscribe();
  };
}

// ---------------------------------------------------------------------------
// Knowledge Spaces: the two-phase access notifier (ADR 0091)
// ---------------------------------------------------------------------------

/**
 * The families whose content follows the viewer's access: narrowed on every
 * narrowing trigger (see
 * docs/plans/2026-10-20-feat-knowledge-spaces-library-and-access-admin-plan.md,
 * section 8). My knowledge is `documents` and
 * `aggregate` under the `ownerPrivate` filter.
 */
export const KNOWLEDGE_CONTENT_FAMILIES = [
  'documents',
  'document-detail',
  'aggregate',
  'search',
  'document-access',
  'document-access-dialog',
  'document-common-use',
  'document-title',
  'picker',
  'sources',
  'space-sources',
  'broadcast-ideas',
  'source-placement',
  'draft-lineage',
  'engagement-label',
  'draft-source-evidence',
  'sent-draft-citations',
  'knowledge-storage',
  'pulse-ai-gated',
] as const satisfies readonly KnowledgeFamily[];

/**
 * Families a helper reads with a forced `fetchQuery` behind a disabled
 * observer, and refreshes from its own listener. While observed they are
 * left to that listener: the notifier cancels them but neither removes nor
 * resets them, so one change makes one replacement read.
 */
export const KNOWLEDGE_HELPER_OWNED_FAMILIES = [
  'picker',
  'document-access-dialog',
  'pulse-ai-gated',
] as const satisfies readonly KnowledgeFamily[];

/** One access change, as the listeners and the caches see it. */
export interface KnowledgeAccessChange {
  /** The office and actor whose entries the change reaches. */
  readonly scope: KnowledgeFamilyScope;
  /** Families that may now hold what the viewer can no longer read. */
  readonly narrow: readonly KnowledgeFamily[];
  /** Further entries to narrow alongside the families. */
  readonly alsoNarrow?: (query: Query) => boolean;
}

/**
 * A surface that holds access-scoped content outside an ordinary observer.
 * `mask` runs synchronously before any cache is touched and must hide what
 * the surface shows without fetching; `refresh` runs after the caches are
 * reset and may start the surface's replacement read.
 */
export interface KnowledgeAccessListener {
  readonly mask: (epoch: number, scope: KnowledgeFamilyScope) => void;
  readonly refresh: (epoch: number, scope: KnowledgeFamilyScope) => void;
}

let accessEpoch = 0;
const listeners = new Set<KnowledgeAccessListener>();

/**
 * Bumped by every access change. A direct cache write reads it before its
 * first await and writes only if it is unchanged after its last.
 */
export function knowledgeAccessEpoch(): number {
  return accessEpoch;
}

export function subscribeToKnowledgeAccessEpoch(listener: KnowledgeAccessListener): () => void {
  // A wrapper per subscription, so one listener subscribed twice is two subscriptions.
  const entry: KnowledgeAccessListener = { mask: listener.mask, refresh: listener.refresh };
  listeners.add(entry);
  return () => {
    listeners.delete(entry);
  };
}

/** A listener's failure must not keep the caches from narrowing; it is rethrown on its own. */
function callEach(
  entries: readonly KnowledgeAccessListener[],
  phase: keyof KnowledgeAccessListener,
  epoch: number,
  scope: KnowledgeFamilyScope,
): void {
  for (const entry of entries) {
    try {
      entry[phase](epoch, scope);
    } catch (error) {
      queueMicrotask(() => {
        throw error;
      });
    }
  }
}

/**
 * Applies an access change, synchronously: bumps the epoch and masks every
 * listener; cancels the affected reads (reverted at once, so no response
 * authorised before the change lands); removes affected entries nothing
 * observes and resets observed ones, which refetch, except observed
 * helper-owned entries; then asks every listener to refresh. Returns the new
 * epoch.
 */
export function notifyKnowledgeAccessChange(
  client: QueryClient,
  change: KnowledgeAccessChange,
): number {
  accessEpoch += 1;
  const epoch = accessEpoch;
  const { scope, narrow, alsoNarrow } = change;
  const entries = [...listeners];
  callEach(entries, 'mask', epoch, scope);

  const affected = (query: Query): boolean =>
    matchesKnowledgeFamilies(query.queryKey, scope, narrow) || (alsoNarrow?.(query) ?? false);
  const leftToHelper = (query: Query): boolean =>
    query.getObserversCount() > 0 &&
    matchesKnowledgeFamilies(query.queryKey, scope, KNOWLEDGE_HELPER_OWNED_FAMILIES);
  if (narrow.length > 0 || alsoNarrow) {
    client.cancelQueries({ predicate: affected }).catch(() => {
      /* a cancelled read reverts; nothing to report */
    });
    client.removeQueries({
      predicate: (query) => affected(query) && query.getObserversCount() === 0,
    });
    client
      .resetQueries({ predicate: (query) => affected(query) && !leftToHelper(query) })
      .catch(() => {
        /* a failed refetch surfaces through its query's own error state */
      });
  }

  callEach(entries, 'refresh', epoch, scope);
  return epoch;
}

/**
 * Marks `families` of one office and actor stale and refetches what is on
 * screen, keeping what it shows meanwhile: for a change that can only widen
 * access.
 */
export async function invalidateKnowledgeFamilies(
  client: QueryClient,
  scope: KnowledgeFamilyScope,
  families: readonly KnowledgeFamily[],
): Promise<void> {
  await client.invalidateQueries({
    predicate: (query) => matchesKnowledgeFamilies(query.queryKey, scope, families),
  });
}

/** The office and actor whose Knowledge Spaces gate is confirmed on, per client. */
const confirmedGates = new WeakMap<QueryClient, { readonly scope: KnowledgeFamilyScope }>();

/**
 * Publishes that the gate is confirmed on for `scope` (null: off, unknown or
 * unconfirmed). The returned function withdraws this publication only, so a
 * later one is never cleared by an earlier cleanup.
 */
export function publishKnowledgeSpacesGate(
  client: QueryClient,
  scope: KnowledgeFamilyScope | null,
): () => void {
  if (scope === null) {
    confirmedGates.delete(client);
    return () => {
      /* nothing was published */
    };
  }
  const publication = { scope };
  confirmedGates.set(client, publication);
  return () => {
    if (confirmedGates.get(client) === publication) {
      confirmedGates.delete(client);
    }
  };
}

/**
 * Under the confirmed gate, a change to the viewer's own membership goes
 * through the notifier: the current office's content families plus the
 * existing selection. False, and nothing done, otherwise.
 */
function notifyGatedMembershipChange(client: QueryClient, actorId: string): boolean {
  const gate = confirmedGates.get(client);
  if (gate?.scope.actorId !== actorId) {
    return false;
  }
  notifyKnowledgeAccessChange(client, {
    scope: gate.scope,
    narrow: KNOWLEDGE_CONTENT_FAMILIES,
    alsoNarrow: (query) => isAccessScopedEntry(query) && query.queryKey.includes(actorId),
  });
  return true;
}
